import type {
    TradeInAemDetailFormItemType,
    TradeInAemNavStep,
    TradeInAemVersion,
    TradeInAemModelSelectType,
} from "./constants";
import type { TradeInAemLabelType } from "./labels";
import type { GdprConsentType } from "../../common-deprecated/utils/contactFormUtils";
import type { TradeInAemExtraVehicleDetailsPdfFiSpecificType } from "../csl/implementations/standalone/fi";
import type { TradeInAemExtraVehicleDetailsPdfGbSpecificType } from "../csl/implementations/standalone/gb";
import type { TradeInAemExtraVehicleDetailsPdfSwSpecificType } from "../csl/implementations/standalone/sw";
import type { TradeInAemExtraVehicleDetailsPdfItSpecificType } from "../csl/implementations/standalone/it";
import type { TradeInAemExtraVehicleDetailsPdfGrSpecificType } from "../csl/implementations/standalone/gr";
import type { TradeInAemExtraVehicleDetailsPdfEsSpecificType } from "../csl/implementations/standalone/es";

// This is the base type of the vehicle object returned from the trade-in API.
// It is unknown on purpose as there really is no consistency across NMSCs.
// We still use the TradeInAemVehicleType to indicate the object is the actual trade-in vehicle and not some other unknown property.
// Always use CSL if you want to interact with a trade-in vehicle.
export type TradeInAemVehicleType = Record<string, unknown> | null;

export type TradeInAemVehicleWithExtraInfoType = Record<string, unknown> | null;

export type TradeInAemFetchVehicleResultsReturnType = {
    results: TradeInAemVehicleType[];
    status?: TradeInAemFindVehicleStatus;
};

// Same concept as TradeInAemVehicleType but for the vehicle valuation.
// This can be a response from the value/quoteId API, however in some cases like TSW it can also be part of the vehicle response.
export type TradeInAemVehicleValuationType = Record<string, unknown> | null;

// Same concept as TradeInAemVehicleType but for a vehicle that is being resolved.
// This type is weak, but in this case it is enough to know this is an object which can contain any other value.
// This object changes every resolve request anyway so stricter typing is difficult, but maybe worth doing for specific CSL implementations.
export type TradeInAemResolveVehicleType = Record<string, any>;

export type TradeInAemVehicleValueResponseType = TradeInAemVehicleValuationType | null | unknown;

export type TradeInAemFetchQuoteFunctionReturnType = Promise<{
    valuation: TradeInAemVehicleValuationType | null;
    latestResponse: TradeInAemVehicleValuationType | unknown;
}>;

// This is the type of the 'tmeExtraInfo' property of the vehicle object
// This property will not be used when calculating the valuation, but we do use it when generating the PDF based on the quoteId
// The naming of this property was discussed with Peter to make sure it doesn't conflict with other properties
// We only add info which can't be linked to user to keep GDPR compliance, therefor we don't add the contact info here
export type TradeInAemVehicleExtraInfoType = {
    selectedCarType: TradeInAemModelSelectType | null;
    selectedModelId: string | null;
    dealerId: string | null;
};

export type TradeInAemParamType = Partial<{
    tradeInStep: TradeInAemNavStep;
    tradeInModalToggleId: string;
    devMode: boolean;
    enablePdf: boolean; // Temporary add 'enablePdf' as query parameter for testing the PDF integrated
    tradeInVersion: TradeInAemVersion;
}>;

export type TradeInAemVehicleNameType = { name: string; description?: string };

export enum TradeInAemResolveItemType {
    Select = "Select",
    Input = "Input",
}

export type TradeInAemResolveItemConfig = {
    type: TradeInAemResolveItemType;
    vehicleProperty: string;
    required: boolean;
    labelKey: TradeInAemLabelType;
    selectedOptionId: string | null;
    options: { label: string; id: string }[];
};

// To avoid complicated typing setups a DetailFormItem is the same across all types.
// If this object becomes too complicated we might need to consider a separate type for each tradeInAemDetailFormItemType.
export type TradeInAemDetailFormItem = {
    type: TradeInAemDetailFormItemType;
    index: number;
    label: string;
    description?: string;
    required?: boolean;
    value: string | string[]; // String if single value, array if multiple values

    // Text specific
    placeholder?: string;
    validationRule?: string;
    validationError?: string; // Localized error if validationRule fails. Required if validationRule has been provided.

    // Multiple choice specific
    options?: { label: string; value: string; description?: string }[];
    defaultValue?: string; // References a value property from the options
};

export enum LeadCategory {
    NewCar = "newCar",
    UsedCar = "usedCar",
}

// If this payload is updated be sure to also update the documentation: https://cde.toyota-europe.com/confluence/display/OR/Trade-in+AEM+frontend+payload
// Also bring Q Team up to date as they will need to support any new/changed properties as well.
export type TradeInAemEHubPayload = {
    brand: string;
    country: string;
    language: string;
    formType: "tradein";
    typeId: "TradeInOfferByEmail";
    // subtype: "standard" | "otpValidation"; // TODO: implement otpValidation subtype in OR-11867
    contactInfo: {
        firstname: string | null;
        lastname: string | null;
        street: string | null;
        housenr: string | null;
        postcode: string | null;
        city: string | null;
        email: string | null;
        phone: string | null;
        dealerId: string | null;
        gdpr: GdprConsentType;
    };
    vehicle: {
        quoteId: string | null;
        licensePlate: string | null;
        vin: string | null;
        name: string | null;
        manufacturer: string | null;
        model: string | null;
        modelYear: string | null;
        doors: string | null;
        fuelType: string | null;
        bodyType: string | null;
        co2: string | null;
        mileage: string | null;
        engine: string | null;
        transmission: string | null;
        pdfLink: string | null;
        registrationDate: string | null;
    };
    vehicleValue: {
        min: number | null;
        max: number | null;
        validityDate: string | null; // ISO timestamp (sourced straight from trade-in API)
    };
    interestedModel: { type: TradeInAemModelSelectType | null; model: string | null; modelCode: string | null };
    leadCategory: LeadCategory;
};

// Base type of properties shown in the PDF extra vehicle details section which are available in multiple countries.
// Only add properties which are available in at least 2 countries
export type TradeInAemExtraVehicleDetailsPdfBaseType = {
    manufacturer: string | null;
    model: string | null;
    grade: string | null;
    generation: string | null;
    engine: string | null;
    powerHp: string | null;
    powerPs: string | null;
    powerKw: string | null;
    body: string | null;
    doors: string | null;
    fuel: string | null;
    transmission: string | null;
    gears: string | null;
    colour: string | null;
    regDate: string | null;
    vin: string | null;
    seats: string | null;
    wheeldrive: string | null;
    cc: string | null;
    year: string | null;
};

export type TradeInAemExtraVehicleDetailsPdfType = TradeInAemExtraVehicleDetailsPdfBaseType &
    Partial<
        | TradeInAemExtraVehicleDetailsPdfFiSpecificType
        | TradeInAemExtraVehicleDetailsPdfGbSpecificType
        | TradeInAemExtraVehicleDetailsPdfSwSpecificType
        | TradeInAemExtraVehicleDetailsPdfItSpecificType
        | TradeInAemExtraVehicleDetailsPdfGrSpecificType
        | TradeInAemExtraVehicleDetailsPdfEsSpecificType
    >;

// All possible keys for the extra vehicle details pdf
// This is used for adding some extra type safety to the label types
export type TradeInAemExtraVehicleDetailsPdfAllPossibleKeysType = keyof (TradeInAemExtraVehicleDetailsPdfBaseType &
    TradeInAemExtraVehicleDetailsPdfFiSpecificType &
    TradeInAemExtraVehicleDetailsPdfGbSpecificType &
    TradeInAemExtraVehicleDetailsPdfSwSpecificType &
    TradeInAemExtraVehicleDetailsPdfItSpecificType &
    TradeInAemExtraVehicleDetailsPdfGrSpecificType &
    TradeInAemExtraVehicleDetailsPdfEsSpecificType);

export type TradeInAemInfoBlockContent = { label: string; value: string }[];

// Structured type used to read the vehicle value from the store.
export type TradeInAemVehicleValueType = {
    value: number;
    validityDate: string;
    quoteId: string;
    // The outstanding loan functionality allows users to add their outstanding loan value to the trade-in valuation. (TGB)
    outstandingLoan?: {
        originalEstimation: number;
        outstandingLoan: number;
    };
};

/**
 *  Used as valid date ranges -> "year": [valid months for that year].
 * @example returns {"2022": [1, 2, 3]}
 */
export type DateRangeMap = Record<string, number[]>;

export enum TradeInAemFindVehicleStatus {
    Loading = "Loading",
    Failed = "Failed",
    Insufficient = "Insufficient",
}
